import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { lazyMore } from '../format';

/**
 * The scroll area of a page. The window itself never scrolls (the schedule
 * fills it, see app.css), so every page that can grow longer than the window
 * brings its own.
 */
export function PageScroll({ children }: { children: ReactNode }): JSX.Element {
  return <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>{children}</div>;
}

/** A full-window page outside the frame (login, account links), centred while it fits and scrolling when it does not. */
export function StandalonePage({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div style={{ height: '100dvh', overflow: 'auto' }}>
      <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--mantine-spacing-md)' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Lazy loading of long lists: how many rows to render, growing by `step`
 * whenever the returned sentinel comes into view. Starts over when `reset`
 * changes (another filter, another list).
 */
export function useLazyCount(total: number, step = 150, reset?: unknown): { shown: number; more: JSX.Element | null } {
  const [shown, setShown] = useState(step);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => setShown(step), [reset, step]);
  const rest = total - shown;
  useEffect(() => {
    const el = ref.current;
    if (!el || rest <= 0 || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setShown((n) => n + step);
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [rest, step]);
  const more = rest > 0
    ? (
      <div ref={ref} style={{ padding: '8px 0', textAlign: 'center', fontSize: 'var(--mantine-font-size-xs)', color: 'var(--mantine-color-dimmed)' }}>
        <button type="button" onClick={() => setShown((n) => n + step)} style={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline' }}>
          {lazyMore(rest)}
        </button>
      </div>
    )
    : null;
  return { shown: Math.min(shown, total), more };
}

/** A marker at the end of a list that calls `onVisible` when it scrolls into view. */
export function Sentinel({ active, onVisible, children }: { active: boolean; onVisible: () => void; children?: ReactNode }): JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null);
  const latest = useRef(onVisible);
  latest.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el || !active || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) latest.current();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [active]);
  if (!active) return null;
  return <div ref={ref} style={{ padding: '8px 0', textAlign: 'center' }}>{children}</div>;
}
